import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ApiCallingV2Service, API_CONFIG, DropdownData } from '@zayan-erp/shared';
import { Observable, map } from 'rxjs';
import {
  DsmCatalogContext,
  DsmCatalogContextQuery,
  DsmCatalogFilters,
  DsmCatalogPage,
  DsmCatalogPdf,
  DsmCatalogPdfQuery,
  DsmCatalogQuery
} from '../models/dsm-options-catalog.models';
import { toCatalogPdfFileName } from '../utils/dsm-options-catalog-params.util';

export const DSM_CATALOG_PAGE_SIZE = 50;

/**
 * The read-only Options Catalog (`api/v2/dsm/options-catalog`). Options always come back grouped —
 * category, then subcategory, then code — so the page can lay pages out under headers as they arrive.
 * Reads are silent: the page shows its own loading, empty and error states.
 */
@Injectable({ providedIn: 'root' })
export class DsmOptionsCatalogService {
  readonly #api = inject(ApiCallingV2Service);
  readonly #http = inject(HttpClient);
  readonly #apiUrl = inject(API_CONFIG).baseUrlV2;
  readonly #baseUrl = `${this.#apiUrl}/dsm/options-catalog`;

  /** The home plans, named, plus their category, subcategory and room lookups (merged across plans). */
  getFilters(context: DsmCatalogContextQuery): Observable<DsmCatalogFilters> {
    const params = new HttpParams({ fromObject: { ...context } });

    return this.#api.getData<DsmCatalogFilters>(`${this.#baseUrl}/filters?${params}`, {
      showLoader: false,
      showMessage: false,
      showError: false
    });
  }

  /** Product types for the Product Type filter. Not home-plan specific, so the catalog API leaves them to the shared lookup. */
  getProductTypes(): Observable<DropdownData[]> {
    return this.#api
      .getData<{ id: string; name: string }[]>(`${this.#apiUrl}/lookup/product-types`, {
        showLoader: false,
        showMessage: false
      })
      .pipe(
        map((types: { id: string; name: string }[]) => (types ?? []).map(({ id, name }) => ({ id, firstLabel: name })))
      );
  }

  /**
   * The query goes as the POST body, so many plans or rooms cannot outgrow the URL; the API reads its enums
   * by name. The page number and size stay in the query.
   */
  getOptionsPage(query: DsmCatalogQuery, pageNumber: number): Observable<DsmCatalogPage> {
    const params = new HttpParams().set('pageNumber', pageNumber).set('pageSize', DSM_CATALOG_PAGE_SIZE);

    return this.#api.postData<DsmCatalogPage>(
      `${this.#baseUrl}/options?${params}`,
      { showLoader: false, showMessage: false, showError: false },
      query
    );
  }

  /**
   * The PDF itself, so it can be previewed and then saved without generating it twice. Errors are left to
   * the caller. `homes` name the file when CORS hides the API's Content-Disposition.
   */
  getOptionsPdf(query: DsmCatalogPdfQuery, homes: DsmCatalogContext[]): Observable<DsmCatalogPdf> {
    return this.#http.post(`${this.#baseUrl}/options/pdf`, query, { responseType: 'blob', observe: 'response' }).pipe(
      map(response => ({
        blob: response.body ?? new Blob([], { type: 'application/pdf' }),
        fileName: this.#fileName(response.headers.get('Content-Disposition')) ?? toCatalogPdfFileName(homes, new Date())
      }))
    );
  }

  /** `attachment; filename=X.pdf; filename*=UTF-8''X.pdf` → `X.pdf` */
  #fileName(contentDisposition: string | null): string | null {
    const match = contentDisposition?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
    return match ? decodeURIComponent(match[1]) : null;
  }
}
